import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { portfolioCompanies } from '../../../mocks/portfolio';
import { useScrollReveal } from '../../../hooks/useScrollReveal';
import { attachTilt } from '../../../hooks/useTiltCards';
import SectionHeading from '../../../components/base/SectionHeading';

export default function PortfolioPreview() {
  const revealRef = useScrollReveal<HTMLDivElement>({ stagger: 0.12 });
  const tiltRef = useRef<HTMLDivElement | null>(null);
  const featured = portfolioCompanies.slice(0, 4);

  useEffect(() => {
    if (!tiltRef.current) return;
    return attachTilt(tiltRef.current, 4);
  }, []);

  return (
    <section
      ref={revealRef}
      data-product-shop
      className="relative bg-[#070709] text-white py-24 md:py-32 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
          <SectionHeading
            eyebrow="Portfolio · selected"
            title="Companies we’re proud to back."
            description="A glimpse at the founders building tomorrow’s infrastructure — across AI, biology, robotics, and space."
          />
          <Link
            to="/investing"
            className="gsap-reveal inline-flex items-center gap-2 text-sm font-medium text-white/80 hover:text-white transition-colors group"
          >
            View all 82 companies
            <i className="ri-arrow-right-up-line transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"></i>
          </Link>
        </div>

        <div ref={tiltRef} className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {featured.map((c) => (
            <article
              key={c.name}
              className="gsap-reveal tilt-card group glass-card hover-glow rounded-2xl overflow-hidden cursor-pointer"
            >
              <div className="relative w-full aspect-[4/3] overflow-hidden">
                <img
                  src={c.image}
                  alt={`${c.name} ${c.sector}`}
                  title={`${c.name} - ${c.sector}`}
                  className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                <div className="absolute top-4 left-4 flex items-center gap-2 tilt-lift">
                  <span className="liquid-glass px-3 py-1 rounded-full text-[11px] font-mono uppercase tracking-widest">
                    {c.stage}
                  </span>
                  <span className="text-[11px] font-mono uppercase tracking-widest text-white/70">
                    · {c.year}
                  </span>
                </div>
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-4 tilt-lift">
                  <div>
                    <h3 className="font-display text-2xl md:text-3xl title-shimmer inline-block">
                      {c.name}
                    </h3>
                    <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/60 mt-1">
                      {c.sector}
                    </div>
                  </div>
                  <div className="w-10 h-10 flex items-center justify-center rounded-full border border-white/20 text-white/80 group-hover:bg-white group-hover:text-black transition-colors">
                    <i className="ri-arrow-right-up-line"></i>
                  </div>
                </div>
              </div>
              <div className="p-6">
                <p className="text-sm text-white/60 leading-relaxed">{c.description}</p>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}